import { apiGet, apiPost, apiPut } from './client';
import { ApiResponse, PaginatedResponse } from '@tinyrewards/shared';

export interface BadgeByCodeInfo {
    badgeID: string;
    badgeName: string;
    badgeImage: string | null;
    issuerName: string | null;
    description: string | null;
    codeType: 'unique' | 'global';
}

export interface BadgeAccessInfo {
    badgeID: string;
    badgeName: string;
    badgeImage: string | null;
    issuerName: string | null;
    badgeShortCode: string | null;
    isPrivate: boolean;
    isAvailableToCodeUsers: boolean;
    isAvailableToDomainUsers: boolean;
    isAvailableToNamedUsers: boolean;
    isUnlimited: boolean;
    limitCount: number;
}

export interface AccessCode {
    UniqueCode: string;
    Status: string | null;
    Email: string | null;
    RedeemedDate: string | null;
    LastModifiedDate: string | null;
}

export interface AccessUser {
    UserID: string;
    Email: string;
    Status: string;
    LastModifiedDate: string | null;
}

export function getBadgeByCode(code: string): Promise<ApiResponse<BadgeByCodeInfo>> {
    return apiGet<BadgeByCodeInfo>(`/api/badge-access/by-code/${encodeURIComponent(code)}`);
}

export function redeemCode(code: string): Promise<ApiResponse<{ message: string }>> {
    return apiPost<{ message: string }>('/api/badge-access/redeem', { code });
}

export function getBadgeAccessInfo(badgeID: string): Promise<ApiResponse<BadgeAccessInfo>> {
    return apiGet<BadgeAccessInfo>(`/api/badge-access/${badgeID}/info`);
}

export function listCodes(badgeID: string, page = 1, pageSize = 25): Promise<PaginatedResponse<AccessCode>> {
    return apiGet<AccessCode[]>(`/api/badge-access/${badgeID}/codes?page=${page}&pageSize=${pageSize}`) as Promise<PaginatedResponse<AccessCode>>;
}

export function listUsers(badgeID: string, page = 1, pageSize = 25): Promise<PaginatedResponse<AccessUser>> {
    return apiGet<AccessUser[]>(`/api/badge-access/${badgeID}/users?page=${page}&pageSize=${pageSize}`) as Promise<PaginatedResponse<AccessUser>>;
}

export function generateCodes(badgeID: string, count: number): Promise<ApiResponse<{ codes: string[] }>> {
    return apiPost<{ codes: string[] }>('/api/badge-access/generate-codes', { badgeID, count });
}

export function addUsers(badgeID: string, emails: string[]): Promise<ApiResponse<{ added: number; messages: string[] }>> {
    return apiPost<{ added: number; messages: string[] }>('/api/badge-access/add-users', { badgeID, emails });
}

export function revokeCodes(badgeID: string, codes: string[]): Promise<ApiResponse<{ messages: string[] }>> {
    return apiPost<{ messages: string[] }>('/api/badge-access/revoke-code', { badgeID, codes });
}

export function revokeUsers(badgeID: string, userIDs: string[]): Promise<ApiResponse<{ messages: string[] }>> {
    return apiPost<{ messages: string[] }>('/api/badge-access/revoke-user', { badgeID, userIDs });
}

export function saveAudience(
    badgeID: string,
    isPrivate: boolean,
    isUnlimited: boolean,
    limitCount?: number,
): Promise<ApiResponse<void>> {
    return apiPut<void>(`/api/badge-access/${badgeID}/audience`, { isPrivate, isUnlimited, limitCount });
}

export function issueManual(
    badgeID: string,
    userEmail: string,
    userFirstName?: string,
    userLastName?: string,
    dateTimeStamp?: string,
    noNotification?: boolean,
): Promise<ApiResponse<{ message: string; issuedID: string }>> {
    return apiPost<{ message: string; issuedID: string }>('/api/badge-access/issue-manual', {
        badgeID,
        userEmail,
        userFirstName,
        userLastName,
        dateTimeStamp,
        noNotification,
    });
}

export type BulkIssueStatus = 'issued' | 'duplicate' | 'error';

export interface BulkIssueResultRow {
    email: string;
    status: BulkIssueStatus;
    issuedID?: string;
    message: string;
}

export interface BulkIssueResult {
    issued: number;
    duplicates: number;
    errors: number;
    results: BulkIssueResultRow[];
}

export function issueManualBulk(
    badgeID: string,
    emails: string[],
    opts?: { dateTimeStamp?: string; skipExisting?: boolean; notify?: boolean },
): Promise<ApiResponse<BulkIssueResult>> {
    return apiPost<BulkIssueResult>('/api/badge-access/issue-manual-bulk', {
        badgeID,
        emails,
        dateTimeStamp: opts?.dateTimeStamp,
        skipExisting: opts?.skipExisting,
        notify: opts?.notify,
    });
}
